import { useSelector } from 'react-redux';
import { Sprite, AnimatedSprite } from '@inlet/react-pixi';
import { getGlobal } from 'store';
import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { FC, useEffect, useRef } from 'react';
import { BubblePerson, SpriteList } from 'domain/bubble';

const SIZE_ASPECT = 0.9;

interface PersonsProps {
  currentPerson: BubblePerson;
  personsSpritesList: SpriteList;
}

export const PersonSprite: FC<PersonsProps> = ({ currentPerson, personsSpritesList }) => {
  const { height, width } = useSelector(getGlobal);

  const personRef = useRef<PIXI.AnimatedSprite>(null);

  const personSprite = personsSpritesList?.[currentPerson.personId!];
  const sprite = personSprite?.[currentPerson.skinId!]
    ? personSprite?.[currentPerson.skinId!]
    : personSprite?.default;
  const isLeft = currentPerson?.position === 'left';

  // SIZE PARAMS
  const imageAspect = sprite.height / sprite.width;
  const spriteHeight = height * SIZE_ASPECT;
  const spriteWidth = spriteHeight / imageAspect;
  const spriteScale: [number, number] = isLeft ? [0, 0] : [-1, 0];

  // POS PARAMS
  const startSpriteX = isLeft ? -width : width + spriteWidth;
  const spriteX = isLeft ? 0 : width / 2 + spriteWidth;
  const spriteY = height * (1 - SIZE_ASPECT);

  useEffect(() => {
    if (sprite.texture) {
      gsap.to(personRef.current, {
        x: startSpriteX,
        duration: 0,
      });
      const anim = gsap.to(personRef.current, {
        x: spriteX,
        duration: 0.5,
      });

      return () => {
        anim.pause();
      };
    }
  }, [sprite.texture]);

  if (!sprite) {
    return null;
  }

  if (!sprite.texture) {
    return null;
  }

  return sprite instanceof PIXI.AnimatedSprite ? (
    <AnimatedSprite
      isPlaying
      textures={[sprite.texture]}
      key={sprite.texture.textureCacheIds[0]}
      scale={spriteScale}
      height={spriteHeight}
      width={spriteWidth}
      x={spriteX}
      y={spriteY}
      ref={personRef}
    />
  ) : (
    <Sprite
      key={sprite.texture.textureCacheIds[0]}
      texture={sprite.texture}
      scale={spriteScale}
      x={spriteX}
      y={spriteY}
      height={spriteHeight}
      width={spriteWidth}
      ref={personRef}
    />
  );
};
